'use client';

import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  navigationMenuTriggerStyle,
} from '@/components/ui/navigation-menu';

import { UserCircleIcon } from '@heroicons/react/24/outline';

import { Sheet, SheetClose, SheetContent, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import {
  Menu,
  Languages,
  LogOut,
  LayoutGrid,
  CreditCard,
  BadgePercent,
  Settings,
  type LucideIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useMemo, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DiscardMultiDeviceDialog from '@/features/app/equipment-data/components/DiscardMultiDeviceDialog';
import usePageNavigation from '@/hooks/usePageNavigation';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useLogoutMutation } from '@/services/reparoly/authApi';
import { logoutAndReset } from '@/lib/logout';
import type { RootState } from '@/app/store/store';
import { hasPermissionCode, POS_SALE_POLICY_READ } from '@/lib/rbac';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useTranslation } from 'react-i18next';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { setLanguage } from '@/store/app/appShellSlice';
import { Separator } from '../ui/separator';

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const brand = useSelector((s: RootState) => {
    const name = s.companyProfile?.name?.trim();
    return name && name.length > 0 ? name : 'Reparoly';
  });
  const profile = useSelector((s: RootState) => s.profile);
  const currentLanguage = useSelector((s: RootState) => s.reparoly.language || 'es');
  const [openDiscardDialog, setOpenDiscardDialog] = useState(false);
  const [discardRedirectTo, setDiscardRedirectTo] = useState<string>('');
  const [sheetOpen, setSheetOpen] = useState(false);
  const pathname = usePathname();
  const { navigateNextPage } = usePageNavigation();
  const dispatch = useDispatch();
  const [logout] = useLogoutMutation();
  // Validación del proceso por path (rutas que forman parte de la recepción)
  const isReceptionPath = useMemo(() => {
    const p = pathname || '';
    if (p.startsWith('/app/client')) return true;
    if (p.startsWith('/app/equipment')) return true;
    if (p.startsWith('/app/budget')) return true;
    if (p.startsWith('/app/signature-consent')) return true;
    return false;
  }, [pathname]);

  const displayName =
    (profile?.display_name || profile?.username || '').trim() ||
    t('NavbarMenu.placeholderName', { defaultValue: 'Usuario' });
  const displayEmail =
    (profile?.email || '').trim() ||
    t('NavbarMenu.placeholderEmail', { defaultValue: 'Sin correo' });
  const avatarUrl = (profile?.avatar_url || '').trim() || undefined;
  const initials = useMemo(() => {
    return displayName
      .split(' ')
      .filter(Boolean)
      .map((word) => word[0])
      .slice(0, 2)
      .join('')
      .toUpperCase();
  }, [displayName]);

  const guardAndNavigate = useCallback(
    (target: string) => {
      if (isReceptionPath) {
        setDiscardRedirectTo(target);
        setOpenDiscardDialog(true);
      } else {
        navigateNextPage(target);
      }
      setSheetOpen(false);
    },
    [isReceptionPath, navigateNextPage],
  );

  const handleLanguageChange = useCallback(
    (lang: string) => {
      dispatch(setLanguage({ value: lang }));
      void i18n.changeLanguage(lang);
    },
    [dispatch, i18n],
  );

  const languageOptions = useMemo(
    () => [
      {
        value: 'es',
        label: t('NavbarMenu.language-es', { defaultValue: 'Español' }),
      },
      {
        value: 'en',
        label: t('NavbarMenu.language-en', { defaultValue: 'English' }),
      },
    ],
    [t],
  );

  const currentLanguageLabel = useMemo(
    () =>
      languageOptions.find((opt) => opt.value === currentLanguage)?.label ||
      currentLanguage?.toUpperCase(),
    [currentLanguage, languageOptions],
  );

  const settingsLinks = useMemo(
    () => {
      const links = [
        {
          key: 'company',
          label: t('NavbarMenu.settings-company', { defaultValue: 'Empresa' }),
          url: '/dashboard/settings/company',
        },
        {
          key: 'users',
          label: t('NavbarMenu.settings-users', { defaultValue: 'Usuarios' }),
          url: '/dashboard/settings/users',
        },
        {
          key: 'order-statuses',
          label: t('NavbarMenu.settings-order-statuses', { defaultValue: 'Estatus de orden' }),
          url: '/dashboard/settings/orden-states',
        },
        {
          key: 'payment-methods',
          label: t('NavbarMenu.settings-payment-methods', { defaultValue: 'Métodos de pago' }),
          url: '/dashboard/settings/payment-methods',
        },
        {
          key: 'pos-ticket-footer',
          label: t('NavbarMenu.settings-pos-ticket-footer', {
            defaultValue: 'Políticas ticket POS',
          }),
          url: '/dashboard/settings/pos-ticket-footer',
        },
        {
          key: 'terms',
          label: t('NavbarMenu.settings-legal-terms', { defaultValue: 'Términos legales' }),
          url: '/dashboard/settings/terms',
        },
      ];
      if (!hasPermissionCode(profile, POS_SALE_POLICY_READ)) {
        return links.filter((link) => link.key !== 'pos-ticket-footer');
      }
      return links;
    },
    [profile, t],
  );

  type DrawerMenuItem = {
    key: string;
    label: string;
    icon: LucideIcon;
    target: string;
    children?: { key: string; label: string; url: string }[];
  };

  const menuItems: DrawerMenuItem[] = useMemo(
    () => [
      {
        key: 'dashboard',
        label: t('NavbarMenu.dashboard', { defaultValue: 'Dashboard' }),
        icon: LayoutGrid,
        target: '/dashboard',
        url: '/dashboard/settings/company',
      },
      {
        key: 'payments',
        label: t('NavbarMenu.payments', { defaultValue: 'Pagos' }),
        icon: CreditCard,
        target: '/dashboard/payments',
      },
      {
        key: 'warrantyPlans',
        label: t('NavbarMenu.warrantyPlans', { defaultValue: 'Planes de garantía' }),
        icon: BadgePercent,
        target: '/dashboard/warranties/plans',
      },
      {
        key: 'settings',
        label: t('NavbarMenu.settings', { defaultValue: 'Configuración' }),
        icon: Settings,
        target: '/dashboard/settings',
        children: settingsLinks,
      },
    ],
    [settingsLinks, t],
  );

  return (
    <header className="bg-white">
      <div className="mx-auto px-3 py-3 lg:ps-8">
        {/* Navbar para Desktop */}
        <NavigationMenu className=" mx-auto max-w-7xl items-center justify-between lg:px-8">
          {/* Izquierda: Logo */}
          {/* Menu hamburguesa para movil */}
          <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="px-0 rounded-md hover:bg-yellow-100 transition-colors group has-[>svg]:px-0"
              >
                <Menu className="size-7 text-black group-hover:text-yellow-500 transition-colors" />
              </Button>
            </SheetTrigger>
            <SheetContent
              side="left"
              className="w-[320px] sm:w-[360px] border-0 bg-transparent p-0 text-left"
            >
              <SheetTitle className="sr-only">
                {t('NavbarMenu.title', { defaultValue: 'Menu' })}
              </SheetTitle>
              <div className="flex h-full flex-col bg-white shadow-2xl">
                <div className="flex flex-col gap-3 px-6 pt-4">
                  <Avatar className="size-20 border-4 border-white shadow-md">
                    {avatarUrl ? <AvatarImage src={avatarUrl} alt={displayName} /> : null}
                    <AvatarFallback className="text-xl font-semibold text-slate-700 bg-slate-100">
                      {initials || 'RP'}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="text-2xl font-semibold text-gray-900">{displayName}</p>
                    <p className="text-sm text-muted-foreground">{displayEmail}</p>
                  </div>
                </div>

                <div className="px-6">
                  <Separator className="my-4" />
                </div>

                <div className="flex flex-1 flex-col gap-3 px-4">
                  {menuItems.map(({ key, label, icon: Icon, target, children }) => {
                    const hasChildren = !!children?.length;

                    if (!hasChildren) {
                      return (
                        <SheetClose asChild key={key}>
                          <button
                            type="button"
                            onClick={() => guardAndNavigate(target)}
                            className="flex w-full items-center justify-between text-left text-base font-semibold text-gray-900"
                          >
                            <span className="flex items-center gap-3">
                              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-slate-100 text-slate-700">
                                <Icon className="h-5 w-5" />
                              </span>
                              <span className="text-left">
                                <p className="text-sm font-semibold">{label}</p>
                              </span>
                            </span>
                          </button>
                        </SheetClose>
                      );
                    }

                    const dropdownTrigger = (
                      <button
                        type="button"
                        className="flex w-full items-center justify-between py-2 text-left text-base font-semibold text-gray-900"
                      >
                        <span className="flex items-center gap-3">
                          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-slate-100 text-slate-700">
                            <Icon className="h-5 w-5" />
                          </span>
                          <span className="text-left">
                            <p className="text-sm font-semibold">{label}</p>
                            <p className="text-xs text-muted-foreground">
                              {t('NavbarMenu.settingsGroup', { defaultValue: 'Configuración' })}
                            </p>
                          </span>
                        </span>
                        <svg
                          className="size-4 text-slate-500"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M6 9l6 6 6-6" />
                        </svg>
                      </button>
                    );

                    return (
                      <DropdownMenu key={key} modal={false}>
                        <DropdownMenuTrigger asChild>{dropdownTrigger}</DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="start"
                          sideOffset={8}
                          className="w-[260px] rounded-2xl border border-slate-100 py-2 shadow-xl"
                        >
                          {children.map((link) => (
                            <DropdownMenuItem
                              key={link.key}
                              onSelect={(event) => {
                                event.preventDefault();
                                guardAndNavigate(link.url);
                              }}
                              className="rounded-xl px-3 py-2 text-sm font-medium text-slate-700 transition data-[highlighted]:bg-slate-100"
                            >
                              {link.label}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    );
                  })}

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        className="flex w-full items-center justify-between text-left text-base font-semibold text-gray-900"
                      >
                        <span className="flex items-center gap-3">
                          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-slate-100 text-slate-700">
                            <Languages className="h-5 w-5" />
                          </span>
                          <span className="text-left">
                            <p className="text-sm font-semibold">
                              {t('NavbarMenu.language', { defaultValue: 'Language' })}
                            </p>
                            <p className="text-xs text-muted-foreground">{currentLanguageLabel}</p>
                          </span>
                        </span>
                        <svg
                          className="size-4 text-slate-500"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M6 9l6 6 6-6" />
                        </svg>
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="start"
                      sideOffset={8}
                      className="w-64 rounded-2xl border border-slate-100 p-2 shadow-xl"
                    >
                      {languageOptions.map((option) => (
                        <DropdownMenuItem
                          key={option.value}
                          onSelect={() => handleLanguageChange(option.value)}
                          className={`flex items-center justify-between rounded-xl px-3 py-2 text-sm font-medium transition ${
                            option.value === currentLanguage
                              ? 'bg-slate-100 text-slate-900'
                              : 'text-slate-600 hover:bg-slate-50'
                          }`}
                        >
                          <span>{option.label}</span>
                          {option.value === currentLanguage ? (
                            <span className="text-xs font-semibold text-emerald-600">●</span>
                          ) : null}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                <div className="px-6 pb-8 pt-4">
                  <SheetClose asChild>
                    <button
                      type="button"
                      onClick={async () => {
                        await logoutAndReset({ dispatch, doLogout: () => logout().unwrap() });
                        navigateNextPage('/auth');
                      }}
                      className="flex w-full items-center justify-center gap-2 rounded-full bg-slate-100 py-3 text-base font-semibold text-gray-800 transition hover:bg-slate-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-200"
                    >
                      <LogOut className="h-5 w-5" />
                      {t('NavbarMenu.signOut', { defaultValue: 'Sign out' })}
                    </button>
                  </SheetClose>
                </div>
              </div>
            </SheetContent>
          </Sheet>

          {/* Centro: Marca y opción Inicio */}
          <NavigationMenuList>
            <NavigationMenuItem>
              <NavigationMenuLink
                asChild
                className={`
                  ${navigationMenuTriggerStyle()}
                  !flex !flex-row items-center
                  gap-2
                  text-xl font-semibold tracking-tight
                  bg-transparnt
                  border-0 shadow-none
                  hover:bg-green-100/25 focus:bg-green-100/25 active:bg-green-100/25
                  focus-visible:ring-0 focus-visible:outline-none`}
              >
                <Link href="/app">{brand}</Link>
              </NavigationMenuLink>
            </NavigationMenuItem>
          </NavigationMenuList>

          {/* Derecha: Perfil + Menu*/}
          <div className="flex justify-end items-center ">
            <a href="#" className="text-sm/6 font-semibold text-gray-900 py-">
              <UserCircleIcon
                aria-hidden="true"
                className="size-8
                                text-black hover:text-yellow-400  transition-colors cursor-pointer
              "
              />
            </a>
          </div>
        </NavigationMenu>
      </div>
      {/* Diálogo de confirmación al ir a Inicio si hay recepción en curso */}
      <DiscardMultiDeviceDialog
        open={openDiscardDialog}
        onOpenChange={setOpenDiscardDialog}
        onStay={() => setOpenDiscardDialog(false)}
        redirectTo={discardRedirectTo}
      />
    </header>
  );
}
